import React, { useMemo, useState } from 'react';
import { useFilters } from '../../contexts/FilterContext';
import { makeCohortScope, resolveTerritoryIds } from '../../utils/filters';
import { ALL_TERRITORY_IDS } from '../../utils/territory';
import { retentionTotal, retentionTrend } from '../../utils/retention';
import { COHORT_SEASONS } from '../../utils/calendar';
import { fmtInt, fmtPct, fmtPP } from '../../utils/format';
import { Panel } from '../ui/Panel';
import { StatCard } from '../ui/StatCard';
import { EmptyState } from '../ui/EmptyState';
import { RetentionControls, type RetentionMode, type YoyWindow } from './RetentionControls';
import { RetentionTrendChart } from './RetentionTrendChart';
import { RetentionBreakdown } from './RetentionBreakdown';
import { DedupExample } from './DedupExample';
interface RetentionViewProps {
  /** Optional drilldown scope; otherwise the global territory / AOP filters apply. */
  territoryIds?: string[];
  aopIds?: string[];
  entityLabel?: string;
}
export function RetentionView({
  territoryIds: forcedTerritories,
  aopIds: forcedAops,
  entityLabel: forcedLabel
}: RetentionViewProps) {
  const {
    filters,
    reset
  } = useFilters();
  const [mode, setMode] = useState<RetentionMode>('season');
  const [seasonBase, setSeasonBase] = useState(9);
  const [seasonComp, setSeasonComp] = useState(10);
  const [yoyBase, setYoyBase] = useState(6);
  const [yoyWindow, setYoyWindow] = useState<YoyWindow>('within12');
  const territoryIds = useMemo(() => forcedTerritories ?? resolveTerritoryIds(filters), [filters, forcedTerritories]);
  const aopIds = forcedAops ?? filters.aops;
  const scope = useMemo(() => makeCohortScope(filters, territoryIds, aopIds), [filters, territoryIds, aopIds]);
  const network = useMemo(() => makeCohortScope(filters, ALL_TERRITORY_IDS, null), [filters]);
  const within12 = mode === 'yoy' && yoyWindow === 'within12';
  const base = mode === 'season' ? seasonBase : yoyBase;
  const comp = mode === 'season' ? seasonComp : yoyBase + 4;
  const gap = comp - base;
  const changeBase = (v: number) => {
    if (mode === 'season') {
      setSeasonBase(v);
      if (seasonComp <= v) setSeasonComp(v + 1);
    } else setYoyBase(v);
  };
  const result = useMemo(() => {
    const cur = retentionTotal(scope, base, comp, within12);
    const net = retentionTotal(network, base, comp, within12);
    const prior = base - 4 >= 0 ? retentionTotal(scope, base - 4, comp - 4, within12) : null;
    const trend = retentionTrend(scope, network, gap, within12);
    return {
      cur,
      net,
      prior,
      trend
    };
  }, [scope, network, base, comp, gap, within12]);
  const pairLabel = within12 ? `${COHORT_SEASONS[base].label} → any program within 12 months` : `${COHORT_SEASONS[base].label} → ${COHORT_SEASONS[comp].label}`;
  const priorLabel = base - 4 >= 0 ? within12 ? `${COHORT_SEASONS[base - 4].short} + 12 mo` : `${COHORT_SEASONS[base - 4].short} → ${COHORT_SEASONS[comp - 4].short}` : null;
  const entityLabel = forcedLabel ?? (territoryIds.length === ALL_TERRITORY_IDS.length && aopIds.length === 0 ? 'All territories' : 'Selected scope');
  const {
    cur,
    net,
    prior
  } = result;
  const yoyChange = prior && prior.base ? cur.rate - prior.rate : null;
  if (territoryIds.length === 0) {
    return <div className="rounded-lg border border-line bg-white" data-id="element-509" data-mp="342wl76jwi691">
        <EmptyState title="No territories match these filters" action={<button type="button" onClick={reset} className="text-xs font-semibold text-navy hover:underline" data-id="element-511" data-mp="nire9vqremm">Reset filters</button>} data-id="element-510" data-mp="394luagnkc9uh" />
      </div>;
  }
  return <div className="space-y-5" data-id="element-512" data-mp="5l0zjy1xpptk">
      <RetentionControls mode={mode} onModeChange={setMode} base={base} onBaseChange={changeBase} comp={seasonComp} onCompChange={setSeasonComp} yoyWindow={yoyWindow} onYoyWindowChange={setYoyWindow} data-id="element-513" data-mp="3nt9p2kpl4fjd" />

      {cur.base === 0 ? <div className="rounded-lg border border-line bg-white" data-id="element-514" data-mp="2gfof4menet5y">
          <EmptyState title="No participants in this base season" description="Territories in scope had not launched yet, or the sport / program filters exclude every participant." data-id="element-515" data-mp="2gctuatrq36c0" />
        </div> : <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-516" data-mp="2mw8d582lr5hw">
            <StatCard label={mode === 'season' ? 'Season Retention' : 'YOY Retention'} value={fmtPct(cur.rate)} emphasis definitionId={mode === 'season' ? 'seasonRetention' : 'yoyRetention'} sub={<span data-id="element-518" data-mp="3andslm4mrvro">
                  {pairLabel}
                  <span className={`ml-1.5 font-semibold ${cur.rate - net.rate < 0 ? 'text-negative' : 'text-positive'}`} data-id="element-519" data-mp="35mzi7xfk49qh">{fmtPP(cur.rate - net.rate)} vs network</span>
                </span>} data-id="element-517" data-mp="2muf5bpg96f8b" />
            <StatCard label="Base season participants" value={fmtInt(cur.base)} definitionId="seasonRetention" sub={<>From <b className="text-ink" data-id="element-521" data-mp="1q8lldngh4bas">{fmtInt(cur.regRows)}</b> registrations, each participant counted once</>} data-id="element-520" data-mp="1u8c91iapebb3" />
            <StatCard label="Returning participants" value={fmtInt(cur.retained)} sub={within12 ? 'Registered for any program within 12 months' : `Also registered in ${COHORT_SEASONS[comp].label}`} data-id="element-522" data-mp="9pigixdmj56z" />
            <StatCard label="YoY change" value={yoyChange === null ? 'n/a' : fmtPP(yoyChange)} sub={priorLabel ? `vs ${priorLabel}: ${prior && prior.base ? fmtPct(prior.rate) : 'no cohort'}` : 'No prior-year cohort in sample data'} data-id="element-523" data-mp="jgwbcrumrgts" />
          </div>

          <div className="grid gap-5 xl:grid-cols-12" data-id="element-524" data-mp="xg5pllza00v4">
            <Panel className="xl:col-span-8" title="Retention trend by base season" definitionId={mode === 'season' ? 'seasonRetention' : 'yoyRetention'} subtitle={within12 ? 'Each base season → any program within 12 months' : `Each base season → ${gap === 1 ? 'the following season' : `${gap} seasons later`}`} data-id="element-525" data-mp="12ephe30jw6ve">
              <RetentionTrendChart points={result.trend} selectedBase={base} entityLabel={entityLabel} data-id="element-526" data-mp="6cir43ib3rm9" />
              {COHORT_SEASONS[comp]?.inProgress && !within12 && <p className="mt-2 text-[11.5px] text-ink-subtle" data-id="element-527" data-mp="k9nqxlrffu92">{COHORT_SEASONS[comp].label} is in progress, so retention into it may still rise as late registrations arrive.</p>}
            </Panel>
            <Panel className="xl:col-span-4" title="One participant, many registrations" subtitle="How participants are counted once (sample)" data-id="element-528" data-mp="pb4phamfv4mh">
              <DedupExample data-id="element-529" data-mp="131871oadia5l" />
            </Panel>
          </div>

          <RetentionBreakdown scope={scope} network={network} base={base} comp={comp} within12={within12} trend={result.trend} pairLabel={pairLabel} data-id="element-530" data-mp="z16b7vrbkay8" />
        </>}
    </div>;
}